import type { Dispatch } from 'redux';
import { apolloClient } from '../api/apolloClient';

export interface WebsocketEvent {
  type: string;
  postId?: string;
  storyId?: string;
  action?: string;
  likeCount?: number;
  bookmarkCount?: number;
  newScore?: number;
  totalReplyCount?: number;
  data?: any;
}

/**
 * Routes known websocket event types to the live-update paths the cache-rendered
 * feed + detail page consume. After TRIL-191 the per-event paths are uniform on
 * the cache side — only one event still dispatches Redux:
 *  - INTERACTION_UPDATED: writes `Post:{id}.viewerInteractions` via cache.modify
 *    only — no Redux dispatch (TRIL-178).
 *  - new_replies: writes `Post:{id}.replyCount` via cache.modify only — no Redux
 *    dispatch (TRIL-191).
 *  - STORY_SCORE_UPDATED: no dispatch — detail-page score reads via Apollo cache
 *    (useStoryScoreUpdates cache.modifies Post:{id}.score, TRIL-178).
 *
 * IMPORTANT for INTERACTION_UPDATED: only writes COUNTS, not hasLiked/
 * hasBookmarked. Those booleans are viewer-specific — User A liking a post
 * should not set hasLiked=true on User B's cache.
 */
export function dispatchWebsocketEvent(
  dispatch: Dispatch,
  event: WebsocketEvent,
): void {
  switch (event.type) {
    case 'INTERACTION_UPDATED': {
      const { postId, action, likeCount, bookmarkCount } = event;
      if (!postId) return;

      // Mirror count-only update into Apollo cache so the detail page
      // (Post:{id}.viewerInteractions) stays current without a refetch.
      // hasLiked/hasBookmarked are intentionally NOT updated here — they
      // are viewer-specific (User A liking should not set hasLiked on
      // User B's cache).
      {
        const patch: Record<string, number> = {};
        if (
          (action === 'like' || action === 'unlike') &&
          typeof likeCount === 'number'
        ) {
          patch.likeCount = likeCount;
        }
        if (
          (action === 'bookmark' || action === 'unbookmark') &&
          typeof bookmarkCount === 'number'
        ) {
          patch.bookmarkCount = bookmarkCount;
        }
        if (Object.keys(patch).length > 0) {
          apolloClient.cache.modify({
            id: apolloClient.cache.identify({ __typename: 'Post', id: postId }),
            fields: {
              viewerInteractions(existing) {
                if (!existing) return existing;
                return { ...existing, ...patch };
              },
            },
          });
        }
      }
      break;
    }

    case 'STORY_SCORE_UPDATED': {
      // The detail page reads score via useQuery(GET_POST_BY_ID_QUERY);
      // useStoryScoreUpdates cache.modifies Post:{id}.score (TRIL-178).
      // posts.store is removed (TRIL-203); no Redux dispatch needed here.
      break;
    }

    case 'new_replies': {
      const { postId, totalReplyCount } = event;
      if (!postId || typeof totalReplyCount !== 'number') return;

      // Absolute set of replyCount on the cached Post entity. The detail
      // page subscribes via useQuery(GET_POST_BY_ID_QUERY); the feed cards
      // subscribe via the normalized Post:{id} reference inside getUserFeed.
      apolloClient.cache.modify({
        id: apolloClient.cache.identify({ __typename: 'Post', id: postId }),
        fields: {
          replyCount: () => totalReplyCount,
        },
      });
      break;
    }

    case 'community_reports_updated': {
      // No Redux dispatch — this event only triggers a re-render of the
      // affected post via EventBus. The Post page subscribes via onEvent
      // and triggers a getPostById refetch.
      break;
    }
  }
}
